import type { InfiniteData } from "@tanstack/vue-query";
import type { Ref } from "vue";
import type { HydraCollection, PartialHydraCollection } from "../Api/Type/hydra.interface";
import { computed, shallowRef, watch } from "vue";

export const DEFAULT_PER_PAGE = 25;
// An overview keeps a sliding window of pages in memory: fetching past either end drops the page at the other
// end. The window bounds what a refetch replays and what the row model has to rebuild, however far the user
// scrolls into a list of thousands of items.
export const OVERVIEW_MAX_PAGES = 12;

export function previousHydraPageParam(firstPageParam: number): number | undefined {
  return firstPageParam > 1 ? firstPageParam - 1 : undefined;
}

// The page param is a number here, but the query options do not carry that type (they are built as a
// plain computed object), so the value is checked rather than trusted.
export function firstPageParamOf(data: InfiniteData<unknown, unknown> | undefined): number | undefined {
  const first = data?.pageParams[0];
  return typeof first === "number" ? first : undefined;
}

// Only the first page of the window carries a total (partial pages skip the count query). The window slides
// away from page 1, so the total is remembered from the last page that reported one and is refreshed whenever
// the window's first page is fetched again.
export function useKnownTotalItems<T>(
  data: Ref<InfiniteData<HydraCollection<T> | PartialHydraCollection<T>, unknown> | undefined>,
): Readonly<Ref<number>> {
  const known = shallowRef(0);

  watch(data, (next) => {
    if (next === undefined) {
      known.value = 0;
      return;
    }
    for (const page of next.pages) {
      if ("totalItems" in page && typeof page.totalItems === "number") {
        known.value = page.totalItems;
        return;
      }
    }
  }, { immediate: true, flush: "sync" });

  return computed(() => known.value);
}

export function extractPageFromHydraNext(next?: string | null): number | undefined {
  if (!next) {
    return undefined;
  }
  try {
    const url = new URL(next, window.location.origin);
    const page = url.searchParams.get("page");
    return page ? Number.parseInt(page, 10) : undefined;
  } catch {
    return undefined;
  }
}

export function paramsToKey(params: URLSearchParams): string {
  const copy = new URLSearchParams(params);
  copy.delete("page");
  copy.delete("partial");
  copy.sort();
  return copy.toString();
}

// A partial page skips the server's count query. The first page of the cached window is fetched in full so a
// refetch of a window that has slid away from page 1 still refreshes the total.
export function isPartialPage(page: number, firstCachedPageParam: number | undefined): boolean {
  return page > 1 && page !== firstCachedPageParam;
}

export function applyPageParams(params: URLSearchParams, page: number, perPage: number, partial: boolean = page > 1): void {
  if (partial) {
    params.append("partial", "true");
  }
  params.append("perPage", String(perPage));
  params.append("page", String(page));
}

export function axiosErrorToState(error: unknown): { errorCode: number | undefined, errorMsg: string } {
  if (typeof error === "object" && error !== null) {
    const maybe = error as { isAxiosError?: boolean, response?: { status?: number }, message?: string };
    if (maybe.isAxiosError || maybe.response?.status != null) {
      return {
        errorCode: maybe.response?.status,
        errorMsg: maybe.message ?? "Unknown error",
      };
    }
    if (error instanceof Error) {
      return { errorCode: undefined, errorMsg: error.message };
    }
  }
  return {
    errorCode: undefined,
    errorMsg: "Unknown error",
  };
}
